import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronRight, Copy, Layers3, Save, Settings2, Sparkles, Trash2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "./ui/breadcrumb";
import { Switch } from "./ui/switch";
import QueryTable from "./QueryTable";
import PromotedPagesTable from "./PromotedPagesTable";
import ReportExportBlock from "./ReportExportBlock";
import BlacklistPagesTable from "./BlacklistPagesTable";
import RepeatStatsTable from "./RepeatStatsTable";
import ReportTable from "./ReportTable";
import PlanFactTab from "./PlanFactTab";
import AuditTab from "./AuditTab";
import CompareDynamicsTab from "./CompareDynamicsTab";
import ScheduledTasksTab from "./ScheduledTasksTab";
import fetchWithAuth from "../fetchWithAuth";
import ProjectNotices from "./ProjectNotices";
import ProjectProcessing from "./ProjectProcessing";
import ProjectControls from "./ProjectControls";
import ProjectScheduleSummary from "./ProjectScheduleSummary";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Textarea } from "./ui/textarea";

const API_URL = "/api/project";

export default function ProjectPage() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editName, setEditName] = useState("");
  const [editClicks, setEditClicks] = useState("");
  const [editGroup, setEditGroup] = useState("");
  const [editRegion, setEditRegion] = useState("213");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [editStagnationLimit, setEditStagnationLimit] = useState();
  const [editDisableLinks, setEditDisableLinks] = useState();
  const [editRepeatErrorScheduledTasks, setEditRepeatErrorScheduledTasks] = useState();
  const [editDeleteOldSchedule, setEditDeleteOldSchedule] = useState(false);
  const [editSearchFullUrl, setEditSearchFullUrl] = useState(false);
  const [editScheduleAlgorithm, setEditScheduleAlgorithm] = useState("standard");
  const [projectAction, setProjectAction] = useState(null);
  const [actionNotice, setActionNotice] = useState("");
  const [actionError, setActionError] = useState("");
  const [activeTab, setActiveTab] = useState("queries");
  const scrollPositionRef = useRef(0);
  const [showSavedDialog, setShowSavedDialog] = useState(false);
  const [copyingTasks, setCopyingTasks] = useState(false);
  const [showCopiedDialog, setShowCopiedDialog] = useState(false);
  const [showCopyFallbackDialog, setShowCopyFallbackDialog] = useState(false);
  const [copyFallbackText, setCopyFallbackText] = useState("");
  const [showDeleteOldTasksDialog, setShowDeleteOldTasksDialog] = useState(false);
  const [deletingOldTasks, setDeletingOldTasks] = useState(false);
  const [showDeleteOldTasksSuccessDialog, setShowDeleteOldTasksSuccessDialog] = useState(false);
  const [deletedOldTasksCount, setDeletedOldTasksCount] = useState(0);
  const [deletedCompletedTasksCount, setDeletedCompletedTasksCount] = useState(0);
  const [deletedPendingTasksCount, setDeletedPendingTasksCount] = useState(0);
  const copyFallbackTextareaRef = useRef(null);

  // Сохранение позиции прокрутки перед обновлением данных
  const saveScrollPosition = () => {
    scrollPositionRef.current = window.scrollY;
  };

  // Восстановление позиции прокрутки после обновления данных
  const restoreScrollPosition = () => {
    window.scrollTo(0, scrollPositionRef.current);
  };

  // Загрузка данных проекта
  const fetchProject = () => {
    saveScrollPosition();
    setLoading(true);
    fetchWithAuth(`${API_URL}/${id}`)
      .then(res => res.json())
      .then(data => {
        setProject(data);
        setEditName(data.name);
        setEditClicks(data.dailyClicks);
        setEditGroup(data.group !== undefined ? data.group : 1);
        setEditRegion(data.region || "213");
        setEditStagnationLimit(data.stagnationLimit !== undefined ? data.stagnationLimit : 4);
        setEditDisableLinks(data.disableLinks !== undefined ? data.disableLinks : true);
        setEditRepeatErrorScheduledTasks(data.repeatErrorScheduledTasks !== undefined ? data.repeatErrorScheduledTasks : false);
        setEditDeleteOldSchedule(Boolean(data.deleteOldSchedule));
        setEditSearchFullUrl(Boolean(data.searchFullUrl));
        setEditScheduleAlgorithm(data.scheduleAlgorithm || "standard");
        setLoading(false);
        // Восстанавливаем позицию прокрутки после обновления данных
        setTimeout(restoreScrollPosition, 0);
      })
      .catch(() => {
        setError("Ошибка загрузки проекта");
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchProject();
    // eslint-disable-next-line
  }, [id]);

  // Устанавливаем document.title в соответствии с заголовком h1
  useEffect(() => {
    if (project && project.name) {
      document.title = `${project.name} — группа #${project.group !== undefined ? project.group : 1}`;
    }
  }, [project]);

  const handleProjectAction = async action => {
    setProjectAction(action);
    setActionError("");
    setError("");
    setActionNotice("");
    try {
      const response = await fetchWithAuth(action === "statistics" ? "/api/arsenkin/statistics" : "/api/projects/bulk-action", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(action === "statistics" ? { projectId: Number(id) } : { action, projectIds: [Number(id)] }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Не удалось выполнить действие");
      setActionNotice(action === "statistics" ? "Сбор выдачи и пересбор статистики поставлены в очередь. Проект остаётся неактивным." :
        action === "stop" ? "Проект остановлен. Все задания удалены из расписания." :
        action === "start" ? "Проект запущен. Обработка аналитики и расписания поставлена в очередь." :
        data.queuedProjectIds?.length ? "Нужна выдача для текущих запросов и настроек. Сбор поставлен в очередь; после него расписание пересоберётся автоматически." : "Расписание пересобрано по актуальной выдаче.");
      fetchProject();
    } catch (error) { setActionError(error.message); }
    finally { setProjectAction(null); }
  };

  // Сохранение изменений
  const handleSave = async () => {
    setSaving(true);
    setError("");
    if (!editName || !editClicks || editGroup === "") {
      setError("Заполните все поля");
      setSaving(false);
      return;
    }
    if (!Number.isInteger(Number(editClicks)) || Number(editClicks) < 10 || Number(editClicks) > 1000) {
      setError("Количество кликов должно быть целым числом от 10 до 1000");
      setSaving(false);
      return;
    }
    if (isNaN(Number(editGroup)) || Number(editGroup) < 1) {
      setError("Группа должна быть положительным числом");
      setSaving(false);
      return;
    }
    if (Number(editStagnationLimit) < 1) {
      setError("Дней до отключения должно быть не меньше 1");
      setSaving(false);
      return;
    }
    if (!/^[0-9{}|]+$/.test(editRegion)) {
      setError("Регион может содержать только цифры, { }, |");
      setSaving(false);
      return;
    }
    let regionBalance = 0;
    for (const char of editRegion) {
      if (char === '{') regionBalance += 1;
      if (char === '}') {
        regionBalance -= 1;
        if (regionBalance < 0) {
          setError("Некорректный формат региона");
          setSaving(false);
          return;
        }
      }
    }
    if (regionBalance !== 0 || !editRegion) {
      setError("Некорректный формат региона");
      setSaving(false);
      return;
    }
    try {
      const res = await fetchWithAuth(`${API_URL}/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: editName,
          dailyClicks: Number(editClicks),
          group: Number(editGroup),
          region: editRegion,
          stagnationLimit: Number(editStagnationLimit),
          disableLinks: Boolean(editDisableLinks),
          repeatErrorScheduledTasks: Boolean(editRepeatErrorScheduledTasks),
          deleteOldSchedule: editDeleteOldSchedule,
          searchFullUrl: editSearchFullUrl,
          scheduleAlgorithm: editScheduleAlgorithm,
        }),
      });
      if (!res.ok) {
        setError("Ошибка при сохранении");
        setSaving(false);
        return;
      }
      const data = await res.json();
      // Вместо setProject(data) — повторно загрузить проект, чтобы получить все поля
      fetchProject();
      setEditName(data.name);
      setEditClicks(data.dailyClicks);
      setEditGroup(data.group !== undefined ? data.group : 1);
      setEditRegion(data.region || "213");
      setEditRepeatErrorScheduledTasks(data.repeatErrorScheduledTasks !== undefined ? data.repeatErrorScheduledTasks : false);
      setShowSavedDialog(true);
      setTimeout(() => setShowSavedDialog(false), 2000);
    } catch {
      setError("Ошибка при сохранении");
    }
    setSaving(false);
  };

  const formatScheduledTimeForCopy = (dateString) => {
    const date = new Date(dateString);
    const pad = (n) => n.toString().padStart(2, "0");
    return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };

  const handleCopyScheduledTasks = async () => {
    let textToCopy = "";
    try {
      setCopyingTasks(true);
      setError("");

      const res = await fetchWithAuth(`/api/project/${id}/scheduled-tasks-copy`);
      if (!res.ok) {
        setError("Ошибка при получении запланированных заданий");
        return;
      }

      const tasks = await res.json();
      textToCopy = tasks
        .map(task => `${task.text}\t${formatScheduledTimeForCopy(task.scheduled_time)}`)
        .join("\n");

      if (!textToCopy) {
        setError("Нет запланированных заданий для копирования");
        return;
      }

      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = textToCopy;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand("copy");
        document.body.removeChild(textArea);

        if (!successful) {
          throw new Error("Clipboard copy failed");
        }
      }

      setShowCopiedDialog(true);
      setTimeout(() => setShowCopiedDialog(false), 2000);
    } catch (e) {
      setCopyFallbackText(textToCopy);
      setShowCopyFallbackDialog(true);
      setTimeout(() => {
        if (copyFallbackTextareaRef.current) {
          copyFallbackTextareaRef.current.focus();
          copyFallbackTextareaRef.current.select();
        }
      }, 0);
    } finally {
      setCopyingTasks(false);
    }
  };

  const handleDeleteOldScheduledTasks = async () => {
    try {
      setDeletingOldTasks(true);
      setError("");

      const res = await fetchWithAuth(`/api/project/${id}/scheduled-tasks-old`, {
        method: "DELETE",
      });

      if (!res.ok) {
        setError("Ошибка при удалении старых заданий");
        return;
      }

      const data = await res.json();
      setDeletedOldTasksCount(Number(data.deletedCount) || 0);
      setDeletedCompletedTasksCount(Number(data.completedCount) || 0);
      setDeletedPendingTasksCount(Number(data.pendingCount) || 0);
      setShowDeleteOldTasksDialog(false);
      setShowDeleteOldTasksSuccessDialog(true);
    } catch {
      setError("Ошибка при удалении старых заданий");
    } finally {
      setDeletingOldTasks(false);
    }
  };

  if (loading) return (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>
  );
  
  if (!project) return (
    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
      Проект не найден
    </div>
  );

  return (
    <div className="space-y-4">
      <Dialog open={showSavedDialog} onOpenChange={setShowSavedDialog}>
        <DialogContent className="flex items-center justify-center bg-white border border-gray-300 rounded-lg shadow-lg px-8 py-6 text-lg font-semibold text-green-700">
          Данные сохранены
        </DialogContent>
      </Dialog>
      <Dialog open={showCopiedDialog} onOpenChange={setShowCopiedDialog}>
        <DialogContent className="flex items-center justify-center bg-white border border-gray-300 rounded-lg shadow-lg px-8 py-6 text-lg font-semibold text-green-700">
          Скопировано в буфер обмена
        </DialogContent>
      </Dialog>
      <Dialog open={showCopyFallbackDialog} onOpenChange={setShowCopyFallbackDialog}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>Не удалось автоматически скопировать</DialogTitle>
            <DialogDescription>
              Ниже подготовлен текст. Нажмите в поле и используйте Ctrl+C для копирования.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            ref={copyFallbackTextareaRef}
            value={copyFallbackText}
            readOnly
            className="min-h-[300px] font-mono text-xs"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCopyFallbackDialog(false)}>
              Закрыть
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={showDeleteOldTasksDialog} onOpenChange={setShowDeleteOldTasksDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Подтверждение удаления</DialogTitle>
            <DialogDescription>
              Будут удалены все старые задания этого проекта до сегодняшнего дня.
              <br />
              <br />
              <span className="text-red-600 font-medium">
                Внимание: если удалить записи до утреннего расчета статистики, данные за предыдущий день могут не попасть в Plan / Fact.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteOldTasksDialog(false)}
              disabled={deletingOldTasks}
            >
              Отмена
            </Button>
            <Button onClick={handleDeleteOldScheduledTasks} disabled={deletingOldTasks}>
              {deletingOldTasks ? "Удаление..." : "Да"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={showDeleteOldTasksSuccessDialog} onOpenChange={setShowDeleteOldTasksSuccessDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Старые задания успешно удалены</DialogTitle>
            <DialogDescription>
              Удалено записей: {deletedOldTasksCount}; из них выполнено заданий: {deletedCompletedTasksCount}, не выполнено заданий: {deletedPendingTasksCount}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setShowDeleteOldTasksSuccessDialog(false)}>
              ОК
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Breadcrumb навигация */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/">Список проектов</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator>
            <ChevronRight className="h-4 w-4" />
          </BreadcrumbSeparator>
          <BreadcrumbItem>
            <BreadcrumbPage>{project.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <h1 className="text-2xl font-bold tracking-tight text-slate-900">
        {project.name} <span className="font-normal text-slate-400">/</span> <span className="font-medium text-slate-500">группа #{project.group ?? 1}</span>
      </h1>

      <div className="grid items-stretch gap-3 lg:grid-cols-[minmax(0,1fr)_auto]">
        <ProjectControls project={project} pendingAction={projectAction} onAction={handleProjectAction} />
        <ReportExportBlock projectId={project.id} compact />
      </div>
      {project.isDraft && <p className="!mt-2 text-xs text-slate-500">Добавьте страницы и запросы, укажите API-ключ Arsenkin — и проект готов к запуску.</p>}
      {!project.isActive && !project.isDraft && <p className="!mt-2 text-xs text-slate-500">Автосборы выключены. Статистику можно обновить вручную кнопкой на панели.</p>}
      {actionError && <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{actionError}</div>}
      {actionNotice && <p role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{actionNotice}</p>}
      <ProjectNotices projectId={id} />
      <ProjectProcessing projectId={id} onComplete={fetchProject} />

      <Card className="overflow-hidden rounded-xl border-slate-200 shadow-sm">
        <CardHeader className="px-4 py-3">
          <CardTitle className="flex items-center gap-2 text-base text-slate-800"><Settings2 className="h-4 w-4 text-indigo-500" />Настройки проекта</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 px-4 pb-4 pt-0">
          <section aria-label="Основные данные проекта" className="rounded-xl border border-sky-100 bg-gradient-to-r from-sky-50 to-indigo-50/70 p-3">
            <h3 className="mb-2.5 flex items-center gap-1.5 text-xs font-semibold text-sky-800"><Layers3 className="h-3.5 w-3.5" />Основные данные</h3>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-12">
              <div className="col-span-2 md:col-span-6">
                <Label htmlFor="name" className="mb-1 block text-xs text-slate-600">Название</Label>
                <Input id="name" className="h-9 border-sky-200/70 bg-white" value={editName} onChange={e => setEditName(e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="group" className="mb-1 block text-xs text-slate-600">Группа</Label>
                <Input id="group" className="h-9 border-sky-200/70 bg-white" type="number" min={1} value={editGroup} onChange={e => setEditGroup(e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="region" className="mb-1 block text-xs text-slate-600">Регион</Label>
                <Input id="region" className="h-9 border-sky-200/70 bg-white" value={editRegion} onChange={e => setEditRegion(e.target.value)} />
              </div>
              <div className="col-span-2 md:col-span-2">
                <Label htmlFor="clicks" className="mb-1 block text-xs text-slate-600">Кликов в день</Label>
                <Input id="clicks" className="h-9 border-sky-200/70 bg-white" type="number" min={10} max={1000} value={editClicks} onChange={e => setEditClicks(e.target.value)} />
              </div>
            </div>
          </section>

          <section aria-label="Генерация расписания" className="rounded-xl border border-violet-100 bg-violet-50/60 p-3">
            <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
              <h3 className="flex items-center gap-1.5 text-xs font-semibold text-violet-800"><Sparkles className="h-3.5 w-3.5" />Алгоритм генерации расписания</h3>
              <span className="rounded-full border border-violet-200 bg-white/80 px-2 py-0.5 text-[10px] text-violet-600">Бусты — отдельно</span>
            </div>
            <div className="grid items-center gap-3 sm:grid-cols-[210px_minmax(0,1fr)]">
              <div>
                <Label htmlFor="scheduleAlgorithm" className="sr-only">Алгоритм генерации расписания</Label>
                <select id="scheduleAlgorithm" value={editScheduleAlgorithm} onChange={e => setEditScheduleAlgorithm(e.target.value)} className="h-9 w-full rounded-md border border-violet-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300">
                  <option value="standard">Стандартный</option>
                  <option value="greedy">Жадный</option>
                  <option value="linear">Линейный</option>
                </select>
              </div>
              <p className="text-xs leading-relaxed text-slate-600">{editScheduleAlgorithm === "standard" ? "Приоритеты топ-20 и ограничение повторов. Квота может выполняться не полностью." : editScheduleAlgorithm === "greedy" ? "Добирает всю квоту по запросам в топ-20, допуская больше двух кликов на пару страница — запрос." : "Делит квоту между всеми активными страницами: сначала топ-20, затем случайное распределение остатка по всем запросам."}</p>
            </div>
            <div role="group" aria-label="Настройка стагнации" className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-violet-200/60 pt-3">
              <div className="flex items-center gap-2">
                <Switch id="disableLinks" checked={!!editDisableLinks} onCheckedChange={setEditDisableLinks} disabled={editDisableLinks === undefined} />
                <Label htmlFor="disableLinks" className="text-xs">Отключать страницы по стагнации</Label>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <Label htmlFor="stagnationLimit" className="text-xs">Дней до отключения</Label>
                <Input id="stagnationLimit" className="h-8 w-16 border-violet-200 bg-white" type="number" min={1} disabled={!editDisableLinks} value={editStagnationLimit ?? ""} onChange={e => setEditStagnationLimit(Math.max(1, Number(e.target.value)))} />
              </div>
              <span className="text-[11px] text-violet-500">Для всех трёх алгоритмов</span>
            </div>
          </section>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-3">
              <div className="flex items-center gap-2">
                <Switch id="searchFullUrl" checked={editSearchFullUrl} onCheckedChange={setEditSearchFullUrl} />
                <Label htmlFor="searchFullUrl" className="text-xs">Искать полный адрес страницы</Label>
              </div>
              <p className="mt-1.5 text-[11px] leading-relaxed text-slate-500">Без протокола, www, параметров и конечного /. Кастомный домен приоритетнее.</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-3">
              <div className="flex items-center gap-2">
                <Switch id="repeatErrorScheduledTasks" checked={!!editRepeatErrorScheduledTasks} onCheckedChange={setEditRepeatErrorScheduledTasks} disabled={editRepeatErrorScheduledTasks === undefined} />
                <Label htmlFor="repeatErrorScheduledTasks" className="text-xs">Перевыполнять задания с ошибками</Label>
              </div>
              <p className="mt-1.5 text-[11px] leading-relaxed text-slate-500">Возвращать задания с ошибкой в очередь на повторное выполнение.</p>
            </div>
          </div>

          <section aria-label="Обслуживание расписания" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-100 bg-amber-50/50 p-3">
            <div>
              <div className="flex items-center gap-2">
                <Switch id="deleteOldSchedule" checked={editDeleteOldSchedule} onCheckedChange={setEditDeleteOldSchedule} />
                <Label htmlFor="deleteOldSchedule" className="text-xs">Ежедневно удалять старое расписание</Label>
              </div>
              <p className="mt-1.5 text-[11px] text-slate-500">Строго до 06:00 МСК. Интервал 06:00–07:00 сохраняется.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={handleCopyScheduledTasks} disabled={copyingTasks} variant="outline" size="sm" className="h-8 bg-white text-xs" aria-label="Скопировать запланированные задания"><Copy className="mr-1.5 h-3.5 w-3.5" />{copyingTasks ? "Копирование…" : "Скопировать задания"}</Button>
              <Button onClick={() => setShowDeleteOldTasksDialog(true)} disabled={deletingOldTasks} variant="outline" size="sm" className="h-8 border-rose-200 bg-white text-xs text-rose-600 hover:bg-rose-50 hover:text-rose-700"><Trash2 className="mr-1.5 h-3.5 w-3.5" />Удалить старые задания</Button>
            </div>
          </section>
          {error && <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
            <p className="text-[11px] text-slate-400">Изменения применяются при следующей генерации или пересборке расписания.</p>
            <Button onClick={handleSave} disabled={saving} size="sm" className="h-9"><Save className="mr-1.5 h-3.5 w-3.5" />{saving ? "Сохранение…" : "Сохранить настройки"}</Button>
          </div>
        </CardContent>
      </Card>

      <ProjectScheduleSummary projectId={project.id} />

      {/* Tabs с контентом */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full min-w-0">
        <div className="max-w-full overflow-x-auto mb-4"><TabsList className="bg-[#3755fa] w-max min-w-full justify-start gap-4 p-1">
          <TabsTrigger 
            value="queries" 
            className="text-white data-[state=active]:bg-white data-[state=active]:text-black"
          >
            Запросы
          </TabsTrigger>
          <TabsTrigger 
            value="promoted" 
            className="text-white data-[state=active]:bg-white data-[state=active]:text-black"
          >
            Продвигаемые страницы
          </TabsTrigger>
          <TabsTrigger 
            value="blacklist" 
            className="text-white data-[state=active]:bg-white data-[state=active]:text-black"
          >
            Вытесняемые страницы
          </TabsTrigger>
          <TabsTrigger 
            value="stats" 
            className="text-white data-[state=active]:bg-white data-[state=active]:text-black"
          >
            Статистика
          </TabsTrigger>
          <TabsTrigger 
            value="report" 
            className="text-white data-[state=active]:bg-white data-[state=active]:text-black"
          >
            Отчет
          </TabsTrigger>
          <TabsTrigger 
            value="scheduled" 
            className="text-white data-[state=active]:bg-white data-[state=active]:text-black"
          >
            Расписание
          </TabsTrigger>
          <TabsTrigger 
            value="planfact" 
            className="text-white data-[state=active]:bg-white data-[state=active]:text-black"
          >
            План / Факт
          </TabsTrigger>
          <TabsTrigger 
            value="audit" 
            className="text-white data-[state=active]:bg-white data-[state=active]:text-black"
          >
            Аудит выдачи
          </TabsTrigger>
          <TabsTrigger 
            value="compare" 
            className="text-white data-[state=active]:bg-white data-[state=active]:text-black"
          >
            Сравнение динамики
          </TabsTrigger>
        </TabsList></div>
        
        <TabsContent value="queries" className="min-w-0 bg-white p-6 rounded-lg shadow">
          <QueryTable
            projectId={project.id}
            queries={project.queries}
            onBulkChange={rows => setProject(current => ({ ...current, queries: rows }))}
            onChange={fetchProject}
          />
        </TabsContent>
        
        <TabsContent value="promoted" className="min-w-0 bg-white p-6 rounded-lg shadow">
          <PromotedPagesTable
            projectId={project.id}
            pages={project.promotedPages}
            blacklistPages={project.blacklistPages}
            onBulkChange={rows => setProject(current => ({ ...current, promotedPages: rows }))}
            onChange={fetchProject}
          />
        </TabsContent>
        
        <TabsContent value="blacklist" className="min-w-0 bg-white p-6 rounded-lg shadow">
          <BlacklistPagesTable
            projectId={project.id}
            groupId={project.group}
            onBulkChange={rows => setProject(current => ({ ...current, blacklistPages: rows }))}
            pages={project.blacklistPages}
            onChange={fetchProject}
          />
        </TabsContent>
        
        <TabsContent value="stats" className="min-w-0 bg-white p-6 rounded-lg shadow">
          <RepeatStatsTable projectId={project.id} />
        </TabsContent>
        
        <TabsContent value="report" className="min-w-0 bg-white p-6 rounded-lg shadow">
          <ReportTable projectId={project.id} />
        </TabsContent>
        <TabsContent value="scheduled" className="min-w-0 bg-white p-6 rounded-lg shadow">
          <ScheduledTasksTab projectId={project.id} />
        </TabsContent>
        <TabsContent value="planfact" className="min-w-0 bg-white p-6 rounded-lg shadow">
          <PlanFactTab projectId={project.id} />
        </TabsContent>
        <TabsContent value="audit" className="min-w-0 bg-white p-6 rounded-lg shadow">
          <AuditTab projectId={project.id} />
        </TabsContent>
        <TabsContent value="compare" className="min-w-0 bg-white p-6 rounded-lg shadow">
          <CompareDynamicsTab 
            projectId={project.id} 
            promotedPages={project.promotedPages} 
            blacklistPages={project.blacklistPages} 
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
